/**
 * Auth-related utility functions
 */

/**
 * Normalizes a base URL and endpoint path to ensure no double slashes
 * @param baseUrl - Base URL (may have trailing slash)
 * @param endpoint - Endpoint path (should start with /)
 * @returns Properly formatted URL without double slashes
 */
export const buildApiUrl = (baseUrl: string, endpoint: string): string => {
  // Remove trailing slashes from base URL
  const normalizedBase = baseUrl.replace(/\/+$/, '');
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${normalizedBase}${normalizedEndpoint}`;
};

/**
 * Extracts a user-friendly error message from backend response data
 * Handles Pydantic validation errors (array of objects) and standard string errors
 */
export const getErrorMessage = (data: unknown, defaultMessage: string): string => {
  console.log('getErrorMessage input:', data);
  try {
    if (!data) return defaultMessage;
    
    // Check if data is an object
    if (typeof data !== 'object' || data === null) {
      return String(data);
    }
    
    // Type guard for object with detail property
    const errorData = data as { detail?: unknown };
    
    if (!errorData.detail) return defaultMessage;
    
    // Handle string error
    if (typeof errorData.detail === 'string') return errorData.detail;
    
    // Handle Pydantic/FastAPI validation errors (array)
    if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
      const firstError = errorData.detail[0];
      
      // Handle the specific Pydantic structure user provided
      // {"type":"string_too_short","loc":["body","password"],"msg":"String should have at least 8 characters",...}
      if (firstError && typeof firstError === 'object') {
        const errorObj = firstError as { msg?: unknown, loc?: unknown[] };
        let message = '';
        
        if ('msg' in errorObj && errorObj.msg) {
           // Clean up "Value error, " prefix if present
          message = String(errorObj.msg).replace(/^Value error,\s*/, '');
        } else {
          message = JSON.stringify(firstError);
        }

        // Prepend field name if available in loc
        if (Array.isArray(errorObj.loc) && errorObj.loc.length > 0) {
          // Get the last item in loc which is usually the field name (e.g. "password" from ["body", "password"])
          const field = errorObj.loc[errorObj.loc.length - 1];
          if (typeof field === 'string' && field !== 'body') {
            // Capitalize first letter
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            // Don't prepend if message already starts with field name
            if (!message.toLowerCase().startsWith(field.toLowerCase())) {
               message = `${fieldName}: ${message}`;
            }
          }
        }
        return message;
      }
      return String(firstError);
    }
    
    // Handle object error (fallback)
    if (typeof errorData.detail === 'object') {
      return JSON.stringify(errorData.detail);
    }
    
    return String(errorData.detail);
  } catch (e) {
    console.error('Error parsing error message:', e);
    return defaultMessage;
  }
};
